import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Dumbbell, TrendingUp } from "lucide-react";
import type { ReactNode } from "react";

import {
  ProgressScreen,
  RecoveryScreen,
  ReminderScreen,
  ScanScreen,
  kilos,
} from "@/features/portal/components/app-screens";
import { Phone } from "@/features/portal/components/devices";
import { Accent, displayTitle, Eyebrow, GoLink, Section } from "@/features/portal/components/portal-ui";
import { FloatChip, Stage } from "@/features/portal/components/stage";
import { photos } from "@/features/portal/content/photos";
import { MuscleMap } from "@/features/training/modules/recovery/components/body-figure";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { Muscle } from "@complete-athlete/enums";

/** What a physio is drawn with: the body, the parts being rehabilitated lit and the rest quiet. */
const REHAB: ReadonlySet<Muscle> = new Set([Muscle.LOWER_BACK, Muscle.HAMSTRINGS, Muscle.QUADS]);
const rehabTone = (muscle: Muscle) => (REHAB.has(muscle) ? "text-primary" : "text-foreground/[0.07]");

const SpecialistCard = ({
  title,
  body,
  tags,
  picture,
  className,
}: {
  title: string;
  body: string;
  tags: string[];
  picture: ReactNode;
  className?: string;
}) => (
  <Link
    to="/coaches"
    className={cn(
      "group dark relative isolate flex h-[440px] flex-col justify-end overflow-hidden rounded-[28px] bg-[oklch(0.13_0.004_120)] p-6 text-foreground ring-1 ring-foreground/[0.07] outline-none ring-inset focus-visible:ring-2 focus-visible:ring-ring md:h-[500px]",
      className,
    )}
  >
    <div className="absolute inset-0 -z-10 transition-transform duration-700 ease-out group-hover:scale-[1.04]">
      {picture}
    </div>
    <span className="absolute inset-0 -z-10 bg-linear-to-t from-[oklch(0.13_0.004_120)] via-[oklch(0.13_0.004_120/0.55)] to-transparent" />
    <span className="absolute top-5 right-5 grid size-11 place-items-center rounded-full bg-white/10 text-white backdrop-blur-xl transition-transform duration-200 group-hover:rotate-45">
      <ArrowUpRight aria-hidden className="size-5" />
    </span>
    <h2 className="text-[30px] leading-none font-medium tracking-[-0.04em]">{title}</h2>
    <p className="mt-3 max-w-[32ch] text-[15px] leading-[1.5] text-pretty text-white/70">{body}</p>
    <ul className="mt-5 flex flex-wrap gap-1.5">
      {tags.map((tag) => (
        <li
          key={tag}
          className="rounded-full bg-white/10 px-3 py-1.5 text-[12px] font-semibold text-white ring-1 ring-white/10 backdrop-blur-xl ring-inset"
        >
          {tag}
        </li>
      ))}
    </ul>
  </Link>
);

const photo = "size-full object-cover";

/**
 * The words that open `/who-its-for`, inside the head card, and the three kinds of specialist as
 * tall cards under them: training and food in photographs, the physio as the body with the parts
 * being rehabilitated lit. Every card leads to the people who can be booked.
 */
export const ForWhomHero = () => (
  <div className="pt-14 md:pt-20">
    <div className="mx-auto max-w-[880px] px-1 text-center">
      <Eyebrow>{m.portal_nav_for_whom()}</Eyebrow>
      <h1 className={cn(displayTitle, "mt-6")}>
        {m.for_whom_title_lead()} <Accent>{m.for_whom_title_tail()}</Accent>
      </h1>
      <p className="mx-auto mt-6 max-w-[54ch] text-lg leading-[1.55] text-pretty text-muted-foreground">
        {m.for_whom_lede()}
      </p>
    </div>
    <div className="mt-12 grid gap-3 md:mt-14 md:grid-cols-3">
      <SpecialistCard
        title={m.coaches_page_role_trainer()}
        body={m.for_whom_trainer_body()}
        tags={[m.for_whom_tag_plan(), m.for_whom_tag_technique(), m.for_whom_tag_live()]}
        picture={<img src={photos.deadliftPlate} alt="" className={photo} />}
      />
      <SpecialistCard
        title={m.coaches_page_role_dietician()}
        body={m.for_whom_dietician_body()}
        tags={[m.for_whom_tag_menu(), m.for_whom_tag_scan(), m.for_whom_tag_macros()]}
        picture={<img src={photos.mealPrep} alt="" className={photo} />}
      />
      <SpecialistCard
        title={m.coaches_page_role_physio()}
        body={m.for_whom_physio_body()}
        tags={[m.for_whom_tag_rehab(), m.for_whom_tag_home(), m.for_whom_tag_recovery()]}
        picture={
          <div className="grid size-full place-items-center bg-[radial-gradient(60%_50%_at_50%_40%,color-mix(in_oklch,var(--primary)_22%,transparent),transparent)] pb-40">
            <MuscleMap toneOf={rehabTone} titleOf={() => ""} className="h-[300px] w-[80%] md:h-[340px]" />
          </div>
        }
      />
    </div>
  </div>
);

const LEVELS = [
  () => m.strength_level_starter(),
  () => m.strength_level_novice(),
  () => m.strength_level_experienced(),
  () => m.strength_level_pro(),
  () => m.strength_level_athlete(),
  () => m.strength_level_elite(),
  () => m.strength_level_olympic(),
];

/** The strength levels as a staircase, one lit, so "every level" reads as a climb. */
const LevelStairs = () => (
  <div aria-hidden className="absolute inset-x-6 bottom-8 flex items-end gap-1.5 md:inset-x-9">
    {LEVELS.map((level, index) => (
      <span key={level()} className="flex flex-1 flex-col items-center gap-2">
        <span
          className={cn(
            "w-full rounded-[12px]",
            index === 2
              ? "bg-primary shadow-[0_14px_40px_-10px_var(--primary)]"
              : index < 2
                ? "bg-primary/35"
                : "bg-foreground/[0.08]",
          )}
          style={{ height: 44 + index * 34 }}
        />
        <small
          className={cn(
            "w-full truncate text-center text-[11px] font-semibold",
            index === 2 ? "text-primary" : "text-muted-foreground",
          )}
        >
          {level()}
        </small>
      </span>
    ))}
  </div>
);

const croppedPhone = "absolute top-0 left-1/2 w-[min(300px,82%)] -translate-x-1/2";

/** What people come for, tile by tile, each on the screen that gets them there. */
export const ForWhomGoals = () => (
  <Section>
    <div className="mb-10 max-w-[780px] px-1">
      <Eyebrow>{m.for_whom_goals_eyebrow()}</Eyebrow>
      <h2 className={cn(displayTitle, "mt-5")}>
        {m.for_whom_goals_lead()} <Accent>{m.for_whom_goals_tail()}</Accent>
      </h2>
    </div>
    <div className="grid gap-3 md:grid-cols-2">
      <Stage
        side
        title={m.for_whom_strength_title()}
        lead={m.for_whom_strength_lead()}
        body={m.for_whom_strength_body()}
        className="md:col-span-2"
        artClassName="flex justify-center px-6 pb-10 lg:items-center lg:py-14"
      >
        <div className="relative w-[min(290px,78%)] lg:w-[310px]">
          <Phone>
            <ProgressScreen />
          </Phone>
          <FloatChip className="top-[64%] -left-[30%] max-lg:hidden">
            <TrendingUp className="size-4 text-primary" />
            {m.features_hero_chip_record({ kg: kilos(92.5) })}
          </FloatChip>
        </div>
      </Stage>
      <Stage lead={m.for_whom_cut_lead()} body={m.for_whom_cut_body()} className="h-[680px] md:h-[620px]" fade>
        <Phone className={croppedPhone}>
          <ScanScreen />
        </Phone>
      </Stage>
      <Stage lead={m.for_whom_mass_lead()} body={m.for_whom_mass_body()} className="h-[680px] md:h-[620px]" fade>
        <Phone className={croppedPhone}>
          <RecoveryScreen />
        </Phone>
      </Stage>
      <Stage
        side
        flip
        title={m.for_whom_rehab_title()}
        lead={m.for_whom_rehab_lead()}
        body={m.for_whom_rehab_body()}
        className="md:col-span-2"
        artClassName="relative grid min-h-[460px] place-items-center px-6 py-10 lg:min-h-0"
      >
        <div aria-hidden className="relative grid w-full max-w-[560px] items-center gap-6 sm:grid-cols-[1fr_1.1fr]">
          <MuscleMap toneOf={rehabTone} titleOf={() => ""} className="h-[320px]" />
          <ul className="grid gap-2">
            {[
              { id: "bridge", name: m.for_whom_rehab_ex_one(), detail: "3 × 12" },
              { id: "dead-bug", name: m.for_whom_rehab_ex_two(), detail: "3 × 10" },
              { id: "split", name: m.for_whom_rehab_ex_three(), detail: "2 × 8" },
            ].map((exercise) => (
              <li
                key={exercise.id}
                className="flex items-center gap-3 rounded-[18px] bg-foreground/[0.06] p-3 ring-1 ring-foreground/[0.06] ring-inset"
              >
                <span className="grid size-10 shrink-0 place-items-center rounded-[12px] bg-primary/15 text-primary">
                  <Dumbbell className="size-[18px]" />
                </span>
                <b className="min-w-0 flex-1 truncate text-[14px] font-semibold">{exercise.name}</b>
                <small className="text-[13px] font-semibold text-muted-foreground tabular-nums">{exercise.detail}</small>
              </li>
            ))}
          </ul>
        </div>
      </Stage>
      <Stage lead={m.for_whom_habit_lead()} body={m.for_whom_habit_body()} className="h-[680px] md:h-[620px]" fade>
        <Phone className={croppedPhone}>
          <ReminderScreen />
        </Phone>
      </Stage>
      <Stage lead={m.for_whom_levels_lead()} body={m.for_whom_levels_body()} className="h-[560px] md:h-[620px]">
        <LevelStairs />
      </Stage>
    </div>
    <div className="mt-10 flex justify-center">
      <GoLink to="/coaches" tone="lime">
        {m.coaches_page_cta()}
      </GoLink>
    </div>
  </Section>
);
